/**
 * Tenant Context AppSync channel — subscribes to ``/default/context/{tenantId}``
 * and fires ``onSyncStatus`` for each ``CONTEXT_SYNC_STATUS`` event.
 *
 * Purpose: flip a Data Plane asset row's lane from spinner → green the instant
 * its async vector indexing completes (REFERENCE), instead of a fake timer. The
 * event is asset-scoped (``{assetId, status, provider}``) and tenant-scoped (not
 * per-network/pool) because an @Tenant asset isn't tied to any one network.
 *
 * Mirrors ``useUserChannel`` (zero local state, fire-once, ref'd callback,
 * multiplexed on the shared appsyncEvents transport). Server side:
 * ``stepflow_core.events.publish_context_sync_status`` +
 * ``appsync_events_authorizer`` (context branch, tenant-match) +
 * ``infra/sync onSubscribe`` (context shape). Best-effort live signal — the
 * catalog's Refresh / ``vectorProviders`` re-fetch is the fallback.
 */
import { useEffect, useRef, useState } from 'react';
import {
  subscribeAppSyncEvents,
  appsyncEventEndpoints,
  type AppSyncEventsUnsubscribe,
} from '../services/appsyncEvents';

export type ContextSyncStatus = {
  eventType: string;
  assetId?: string;
  status?: string; // 'SYNCED' | 'FAILED'
  provider?: string;
  layer?: string;
  networkId?: string;
};

// Coarse landing-dashboard "live" beacon carried on the SAME tenant context
// channel (LANDING) — a room went RUNNING/IDLE. Every tenant member watching the
// dashboard receives it (collaborative), unlike the per-user channel.
export type RoomActivity = {
  networkId?: string;
  poolId?: string;
  state?: string; // 'RUNNING' | 'IDLE'
};

function parseEntry(e: unknown): Record<string, unknown> | null {
  if (typeof e === 'string') {
    try {
      const p = JSON.parse(e);
      return p && typeof p === 'object' && !Array.isArray(p)
        ? (p as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (e && typeof e === 'object' && !Array.isArray(e)) {
    return e as Record<string, unknown>;
  }
  return null;
}

/**
 * Subscribe to the tenant Context channel. Pass ``undefined`` for ``tenantId``
 * to disable. ``onSyncStatus`` is captured via a ref so a fresh closure each
 * render won't retrigger the subscription.
 */
export function useContextChannel(
  tenantId: string | undefined,
  onSyncStatus: (s: ContextSyncStatus) => void,
  onRoomActivity?: (a: RoomActivity) => void,
) {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cbRef = useRef(onSyncStatus);
  useEffect(() => {
    cbRef.current = onSyncStatus;
  }, [onSyncStatus]);
  const roomCbRef = useRef(onRoomActivity);
  useEffect(() => {
    roomCbRef.current = onRoomActivity;
  }, [onRoomActivity]);

  const { httpDns, realtimeDns } = appsyncEventEndpoints();

  useEffect(() => {
    const t = (tenantId || '').trim();
    if (!t || !httpDns || !realtimeDns) {
      setConnected(false);
      return undefined;
    }
    setError(null);
    let active = true;
    const channelPath = `/default/context/${t}`;

    const unsubscribe: AppSyncEventsUnsubscribe = subscribeAppSyncEvents(
      httpDns,
      realtimeDns,
      channelPath,
      {
        onData: (list) => {
          if (!active) return;
          for (const entry of list) {
            const env = parseEntry(entry);
            if (!env) continue;
            const et = String(env.eventType || '');
            if (et === 'ROOM_ACTIVITY_CHANGED') {
              // Landing-dashboard live beacon (LANDING). Optional consumer.
              try {
                roomCbRef.current?.({
                  networkId: typeof env.networkId === 'string' ? env.networkId : undefined,
                  poolId: typeof env.poolId === 'string' ? env.poolId : undefined,
                  state: typeof env.state === 'string' ? env.state : undefined,
                });
              } catch (e) {
                console.warn('[useContextChannel] onRoomActivity threw:', e);
              }
              continue;
            }
            if (et !== 'CONTEXT_SYNC_STATUS') continue;
            try {
              cbRef.current({
                eventType: 'CONTEXT_SYNC_STATUS',
                assetId: typeof env.assetId === 'string' ? env.assetId : undefined,
                status: typeof env.status === 'string' ? env.status : undefined,
                provider: typeof env.provider === 'string' ? env.provider : undefined,
                layer: typeof env.layer === 'string' ? env.layer : undefined,
                networkId: typeof env.networkId === 'string' ? env.networkId : undefined,
              });
            } catch (e) {
              console.warn('[useContextChannel] onSyncStatus threw:', e);
            }
          }
        },
        onStatus: (status) => {
          if (!active) return;
          setConnected(status === 'connected');
        },
        onError: (err) => {
          if (!active) return;
          setError(typeof err === 'string' ? err : JSON.stringify(err ?? {}));
        },
      },
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [tenantId, httpDns, realtimeDns]);

  return { connected, error };
}
